// "Your allowance is spent" — the slip shown inside the gloss popover when a
// translation is refused.
//
// The design language is the library's own, continued one step: the purchase
// plate is a wax seal, the library is shelved boards, and this is the DATE-DUE
// SLIP pasted inside a borrowed book — a dashed rule, a rubber-stamped return
// time set in Rockwell numerals at a slight rotation. It reads as part of the
// book rather than as an error, which is the whole point: running out of an
// hourly allowance is an ordinary moment in a reading session, not a failure.
//
// Deliberately NOT a modal, a toast, or a full-card takeover. The reader was
// mid-sentence and asked one small question; the answer is small, sits in the
// space the gloss would have occupied, and never moves the passage underneath.
//
// The OFFER is conditional and the slip is not. A reader who has hit the cap of
// a plan they already pay for gets the wait and no sales pitch — see `offer`.

import { Link } from "react-router";
import { Stamp } from "lucide-react";
import { Button } from "@bindery/ui/components/ui/button";
import { formatPrice } from "@/lib/price";
import { retryClock, retryPhrase, type QuotaRefusal } from "@/lib/quota-error";

/** The plan to offer, already chosen by the caller — `null` when there is
 *  nothing honest to sell this reader. */
export interface PromptOffer {
  planId: string;
  displayName: string;
  amountCents: number;
  currency: string;
}

/**
 * The refusal slip. `now` comes from the caller's coarse clock so the wording
 * ("in 12 minutes") stays honest as the popover sits open, without this
 * component owning a timer of its own.
 */
export function SubscribePrompt({
  refusal,
  now,
  offer,
  next,
  onSubscribeClick,
}: {
  refusal: QuotaRefusal;
  now: number;
  /**
   * The upgrade worth offering, or `null` when the reader already holds
   * everything that would help. Passing `null` is the difference between "here
   * is a way past this" and trying to sell somebody the plan they are already
   * paying for — which is exactly what a refusal on a PAID tier would do, and
   * the daily cap only exists on paid tiers at all.
   */
  offer: PromptOffer | null;
  /** Where to come back to after subscribing — the passage being read. */
  next: string;
  onSubscribeClick?: () => void;
}) {
  const daily = refusal.scope === "day";

  return (
    <div className="border-border flex flex-col items-start gap-3 border-t border-dashed pt-3">
      <div className="flex w-full items-start gap-3">
        {/*
          The rubber stamp. Rotated a couple of degrees and drawn in the muted
          ink the eyebrows use — a date-due impression, not a badge.
        */}
        <span
          aria-hidden="true"
          className="border-muted-foreground/50 text-muted-foreground/70 mt-0.5 flex shrink-0 -rotate-2 items-center justify-center rounded-sm border border-dashed px-1.5 py-1"
        >
          <Stamp className="size-3.5" />
        </span>
        <div className="min-w-0">
          <p className="text-muted-foreground font-serif text-xs tracking-[0.28em] uppercase">
            {daily ? "Out for today" : "Allowance spent"}
          </p>
          <p className="text-foreground mt-1.5 font-serif text-sm leading-relaxed">
            {daily
              ? `That's ${String(refusal.limit)} translations today.`
              : `That's ${String(refusal.limit)} translations this hour.`}{" "}
            <span className="text-muted-foreground">
              Cached passages stay free to read.
            </span>
          </p>
          <p className="text-muted-foreground mt-1 font-serif text-xs">
            Back{" "}
            <span className="font-rockwell text-foreground tabular-nums">
              {retryPhrase(refusal.retryAt, now)}
            </span>
            {/*
              The clock time is shown for BOTH scopes. It was hidden on the
              daily one, which is precisely the case where "tomorrow" is least
              trustworthy: the day rolls at UTC midnight, so for a reader far
              enough east "tomorrow" is this afternoon.
            */}
            {" · "}
            <span className="font-rockwell tabular-nums">
              {retryClock(refusal.retryAt)}
            </span>
          </p>
        </div>
      </div>

      {offer !== null && (
        <Button asChild size="sm" className="w-full" onClick={onSubscribeClick}>
          <Link to={`/subscribe?next=${encodeURIComponent(next)}`}>
            {offer.displayName} —{" "}
            {formatPrice(offer.amountCents, offer.currency)}/month
          </Link>
        </Button>
      )}
    </div>
  );
}
